import baseAPI, { OtpChallengeView } from 'api';
import { AxiosError } from 'axios';
import { WisoryLabel, FormItem, WisoryInput } from 'components/Inputs';
import { PrevButton } from 'components/PrevButton';
import { WisoryButton } from 'components/WisoryButton';
import { BookingFormLayout } from 'pages/profile/components/Layout';
import { ProfileFooter } from 'pages/profile/components/Layout/components/ProfileFooter';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { FiAlertTriangle } from 'react-icons/fi';
import { EMAIL_REGEX } from 'utils/regex';

type Props = {
  title: string;
  useCognitoOtpLogin: boolean;
  onSubmit: (email: string, cognitoData?: OtpChallengeView) => void;
  onGoBack: VoidFunction;
};

type FormParams = { email: string };

export function EmailForm({ useCognitoOtpLogin, title, onSubmit, onGoBack }: Props) {
  const { t } = useTranslation();

  const [submitting, setSubmitting] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormParams>();

  const onFormSubmit = async ({ email }: FormParams) => {
    if (submitting) {
      return;
    }

    setSubmitting(true);

    try {
      if (useCognitoOtpLogin) {
        let challengeView = await baseAPI.login.otpLogin({
          emailAddress: email,
        });
        onSubmit(email, challengeView.data);
      } else {
        await baseAPI.login.sendOtp({
          emailAddress: email,
        });
        onSubmit(email);
      }
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 404) {
        setErrorMessage(t('Invalid email address'));
      } else {
        setErrorMessage(t(`The system didn't respond in time, please try again later`));
      }
    }

    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <BookingFormLayout
        footer={
          <ProfileFooter className="flex justify-between">
            <PrevButton type="button" loading={submitting} onClick={onGoBack}>
              {t('REACT_BOOKING_FRONTEND.common.prevButton')}
            </PrevButton>
            <WisoryButton variant="primary" className="md:mt-0 md:ml-5 max-w-xs" type="submit" loading={submitting}>
              {t('REACT_BOOKING_FRONTEND.booking2.emailScreenSendCode')}
            </WisoryButton>
          </ProfileFooter>
        }
      >
        <div className="wisory-title mb-2">{title}</div>

        <div className="font-openSans mb-10 text-black">
          <Trans t={t} i18nKey="REACT_BOOKING_FRONTEND.booking2.emailScreenSubtitle" />
        </div>
        <div>
          <WisoryLabel className="mb-3">
            {t('REACT_BOOKING_FRONTEND.checkoutScreen.payWithInvoice.emailAddress')}
          </WisoryLabel>
          <div className="mt-2 w-full">
            <FormItem errorMessage={errors.email?.message}>
              <WisoryInput
                type="email"
                autoFocus
                {...register('email', {
                  required: t('REACT_BOOKING_FRONTEND.common.validations.required'),
                  pattern: {
                    value: EMAIL_REGEX,
                    message: t('REACT_BOOKING_FRONTEND.common.validations.email'),
                  },
                })}
              />
            </FormItem>
          </div>
        </div>

        {errorMessage && (
          <div className="mt-5 text-sm font-openSans flex justify-center items-center">
            <div className="pr-5">
              <FiAlertTriangle style={{ fontSize: 30 }} className="text-error" />
            </div>
            <div>
              {t(errorMessage)}
              {'  '}
              <a
                className="underline text-yellow hover:text-denim transition-colors"
                target="_blank"
                rel="noreferrer"
                href={`mailto:${WISORY_SDK_SETTINGS.CONTACT_EMAIL}?subject=SKAPA%20ANV%C3%84NDARE&body=Hej%20Wisory%20Happiness%20Team%2C%0D%0A%0D%0AJag%20%C3%B6nskar%20att%20ni%20skapar%20min%20anv%C3%A4ndarprofil%20s%C3%A5%20jag%20kan%20komma%20ig%C3%A5ng%20med%20tj%C3%A4nsten%3A%0D%0AJag%20heter%3A%20%0D%0AMitt%20f%C3%B6retag%2Forganisation%3A%20%0D%0AMobilnr%3A%20%0D%0A%0D%0ATack%20p%C3%A5%20f%C3%B6rhand%21`}
              >
                {t('login_customer_contact_link_text')}
              </a>
            </div>
          </div>
        )}
      </BookingFormLayout>
    </form>
  );
}
